import { gsap, ScrollTrigger, SplitText, prefersReducedMotion, transition } from './gsap'

/*
  Animações declarativas por atributo. Chame setupReveals(root) dentro de um useGSAP() da página.

  data-split           Título que sobe linha por linha (com máscara) ao entrar na tela
  data-reveal          Elemento que aparece subindo; vizinhos entram em sequência
  data-clip            Imagem/bloco que se abre a partir do centro
  data-parallax="10"   Imagem que se move mais devagar que a rolagem (valor = intensidade)
  data-words           Parágrafo que "acende" palavra por palavra conforme a rolagem
  data-counter="24"    Número que conta de 0 até o valor
*/
export function setupReveals(root) {
  if (!root || prefersReducedMotion()) return

  const q = gsap.utils.selector(root)
  const setupTime = performance.now()
  // Elementos já visíveis ao abrir a página esperam a cortina de transição sair.
  const enterDelay = () => (performance.now() - setupTime < 300 ? transition.delay : 0)

  // Títulos linha por linha
  q('[data-split]').forEach((el) => {
    let done = false
    const split = SplitText.create(el, {
      type: 'lines',
      mask: 'lines',
      autoSplit: true,
      onSplit(self) {
        if (!done) gsap.set(self.lines, { yPercent: 110 })
      },
    })
    ScrollTrigger.create({
      trigger: el,
      start: 'top 88%',
      once: true,
      onEnter: () =>
        gsap.to(split.lines, {
          yPercent: 0,
          duration: 1.1,
          ease: 'expo.out',
          stagger: 0.08,
          delay: enterDelay(),
          onComplete: () => {
            done = true
            split.revert()
          },
        }),
    })
  })

  // Elementos que sobem em sequência
  const reveals = q('[data-reveal]')
  if (reveals.length) {
    // transition: none evita que as transições CSS (hover etc.) atrasem a animação do GSAP
    gsap.set(reveals, { autoAlpha: 0, y: 40, transition: 'none' })
    ScrollTrigger.batch(reveals, {
      start: 'top 90%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          stagger: 0.1,
          delay: enterDelay(),
          overwrite: true,
          clearProps: 'transform,transition',
        }),
    })
  }

  // Abertura de imagens
  q('[data-clip]').forEach((el) => {
    gsap.fromTo(
      el,
      { clipPath: 'inset(14% 14% 14% 14%)' },
      {
        clipPath: 'inset(0% 0% 0% 0%)',
        duration: 1.4,
        ease: 'expo.out',
        clearProps: 'clipPath',
        scrollTrigger: { trigger: el, start: 'top 85%', once: true },
      }
    )
  })

  // Parallax
  q('[data-parallax]').forEach((el) => {
    const amount = Number(el.dataset.parallax) || 10
    gsap.set(el, { scale: 1 + amount / 50 })
    gsap.fromTo(
      el,
      { yPercent: -amount },
      {
        yPercent: amount,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
      }
    )
  })

  // Texto que acende com a rolagem
  q('[data-words]').forEach((el) => {
    const split = SplitText.create(el, { type: 'words' })
    gsap.fromTo(
      split.words,
      { opacity: 0.15 },
      {
        opacity: 1,
        stagger: 0.1,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top 80%', end: 'bottom 55%', scrub: true },
      }
    )
  })

  // Contadores
  q('[data-counter]').forEach((el) => {
    const end = Number(el.dataset.counter)
    const counter = { value: 0 }
    el.textContent = '0'
    ScrollTrigger.create({
      trigger: el,
      start: 'top 92%',
      once: true,
      onEnter: () =>
        gsap.to(counter, {
          value: end,
          duration: 1.8,
          ease: 'power2.out',
          delay: enterDelay(),
          onUpdate: () => {
            el.textContent = Math.round(counter.value)
          },
        }),
    })
  })

  // Recalcula as posições quando as imagens terminam de carregar (elas mudam a altura da página)
  const refresh = gsap.delayedCall(0.15, () => ScrollTrigger.refresh()).pause()
  q('img').forEach((img) => {
    if (!img.complete) img.addEventListener('load', () => refresh.restart(true), { once: true })
  })
}
